import { getProvider, providersByReadiness } from "@aux0/connectors";
import { createFileRoute } from "@tanstack/react-router";
import { ProviderMark } from "@/components/common/badges";
import { HostedAuthFlow } from "@/components/connect/hosted-auth-flow";

/**
 * The hosted auth page, as the customer's end user sees it.
 *
 * It lives outside the dashboard shell on purpose: this page is served on the
 * customer's own subdomain with their branding, and Aux0 appears on it only in
 * the footer line the providers require. It is in this app so the flow can be
 * designed and screenshotted alongside everything else.
 */
export const Route = createFileRoute("/auth/$linkId")({
  component: HostedAuthPage,
});

function HostedAuthPage() {
  // The five an end user is most likely to be asked for, in catalog order.
  const offered = ["gmail", "outlook", "icloud", "google-calendar", "google-contacts"]
    .map((id) => getProvider(id))
    .filter((provider) => provider !== undefined);

  const live = providersByReadiness().filter(
    (provider) => provider.status !== "planned",
  );

  return (
    <main className="flex min-h-svh flex-col items-center bg-surface px-4 py-10">
      <div className="w-full max-w-[440px]">
        {/* The customer's brand, not Aux0's. These three lines are what the
            `branding` object on the project sets. */}
        <header className="mb-6 flex flex-col items-center gap-2.5 text-center">
          <span
            aria-hidden
            className="flex size-10 items-center justify-center rounded-xl bg-primary text-[15px] font-bold text-primary-foreground"
          >
            N
          </span>
          <h1 className="text-[19px] font-semibold tracking-[-0.015em]">
            Connect your accounts to Northwind
          </h1>
          <p className="max-w-[36ch] text-[13px] leading-[1.55] text-muted-foreground">
            Northwind will be able to read and send mail, and read your calendar and
            contacts, on your behalf. You can disconnect any account at any time.
          </p>
        </header>

        <HostedAuthFlow providers={offered} allProviders={live} />

        <footer className="mt-6 flex flex-col items-center gap-1.5 text-center">
          <p className="text-[11.5px] text-muted-foreground">
            Connected securely by{" "}
            <span className="font-semibold text-foreground">
              Aux<span className="text-brand-gradient">0</span>
            </span>
            . Your password is never shared with Northwind.
          </p>
          <p className="flex items-center gap-2 text-[10.5px] text-ink-faint">
            {offered.slice(0, 5).map((provider) => (
              <ProviderMark key={provider.id} provider={provider.id} size="sm" />
            ))}
          </p>
        </footer>
      </div>
    </main>
  );
}
